import { httpsCallable } from 'firebase/functions';
import type { TenantCustomizations, TenantFeature } from '@kawai/shared';
import { functions } from '../firebase.js';

export interface TenantCreateInput {
  tenantId: string;
  name: string;
  shopName: string;
  features: TenantFeature[];
  customizations?: TenantCustomizations;
  contractStartedAt?: string;
}

export interface TenantCreateResponse {
  ok: true;
  tenantId: string;
}

const tenantCreateCallable = httpsCallable<
  TenantCreateInput,
  TenantCreateResponse
>(functions, 'tenantCreate');

/**
 * S-06: `tenantCreate` Callable の薄いラッパ。
 * Functions 側で saas-admin role / zod 検証 / 重複検出を行い、
 * 失敗時は FunctionsError を throw する。
 */
export async function callTenantCreate(
  input: TenantCreateInput,
): Promise<TenantCreateResponse> {
  const res = await tenantCreateCallable(input);
  return res.data;
}

/**
 * Firebase Functions の Callable エラー（FunctionsError）相当かを duck typing で判定する。
 *
 * `instanceof FunctionsError` は firebase SDK のバージョン差・bundle 重複・realm 跨ぎで
 * 不安定になりうる（S-06b #4）。本番の FunctionsError は `code` が `functions/<reason>`
 * 形式を持つため、その形を満たすかで判定する。これによりテストは SDK 内部コンストラクタに
 * 依存せず、同形の plain object を投げて検証できる。
 */
interface FunctionsErrorLike {
  code: string;
  message?: string;
}

function isFunctionsErrorLike(err: unknown): err is FunctionsErrorLike {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string' &&
    (err as { code: string }).code.startsWith('functions/')
  );
}

/**
 * `tenantCreate` のエラーを saas-admin 向け日本語メッセージに変換。
 *
 * - already-exists: tenantId 重複（フォーム上で「他の ID にしてください」を促す）
 * - invalid-argument: 入力値検証 NG（メッセージはそのまま見せる）
 * - permission-denied: saas-admin 以外がリクエスト
 * - unauthenticated: セッション切れ
 */
export function tenantCreateErrorMessage(err: unknown): string {
  if (isFunctionsErrorLike(err)) {
    switch (err.code) {
      case 'functions/already-exists':
        return 'この tenantId は既に使われています。別の ID を指定してください。';
      case 'functions/invalid-argument':
        return `入力値に誤りがあります：${err.message ?? ''}`;
      case 'functions/permission-denied':
        return '権限がありません（saas-admin 権限が必要です）。';
      case 'functions/unauthenticated':
        return 'セッションが切れました。再ログインしてください。';
      default:
        return `テナント作成に失敗しました（${err.code}）。`;
    }
  }
  if (err instanceof Error) {
    return `テナント作成に失敗しました：${err.message}`;
  }
  return 'テナント作成に失敗しました。';
}
